import createDebug from 'debug';
import { Deserializer } from "jsonapi-serializer";
import Commands from '../constants/commands';
import { CREATED, INTERNAL_SERVER_ERROR, NO_CONTENT } from "../constants/response-status-codes";
import { HidMobileAccessSubCmds, StidMobileSubCmds } from '../constants/sub-commands';
import { ACCOUNT_TYPES } from '../constants/account-constants';
import { TranslationKeys } from "../contexts/TranslationKeys";
import { MobileAccount } from "../models/Account";
import { genericError } from "../models/GenericError";
import { ListQuery } from "../models/ListQuery";
import { MobileAccess, MobileAccessWrapper } from "../models/MobileAccess";
import { Name } from "../models/Name";
import { PaginationModel } from "../models/Pagination";
import { StoreEntityResult } from "../models/services/StoreEntityResult";
import { MobileAccountWrapper } from "../models/wrappers/MobileAccountWrapper";
import { APIConnector } from "./api/APIConnector";
import { processServerError, getConstraintViolation } from "./utilities/ServiceError";
import { getActiveStatus } from "./utilities/StatusBuilder";
import { createPagination } from "./utilities/paginationBuilder";
import { createPartialURL, getFilterClause } from "./utilities/urlBuilder";
import { Translator } from '../contexts/translator';
import {IdentityProviderGroup, IdentityProviderGroupWrapper} from "@/models/IdentityProviderGroup.ts";

const apiConnector = new APIConnector();
const debug = createDebug('account-service');

/**
 * This method is actually used to fetch all system accounts, not just mobile accounts.
 * @param listParameters
 * @returns
 */
export const getMobileAccounts = async (listParameters?: ListQuery): Promise<MobileAccountWrapper> => {
    try {
        const filter: string = getFilterClause(listParameters?.filters, listParameters?.searchTerm);
        const filterAccountType = `filter=accountType.id=in=(${ACCOUNT_TYPES.ID_HID_MOBILE_ACCESS},${ACCOUNT_TYPES.ID_STID_MOBILE_ID},${ACCOUNT_TYPES.ID_MQTT_BROKER},${ACCOUNT_TYPES.ID_LDAP},${ACCOUNT_TYPES.ID_VINGCARD},${ACCOUNT_TYPES.ID_AUTH})`;
        const filterOutNonMobileAccounts = `${filter ? filter.replace('filter=', `${filterAccountType};`) : `&${filterAccountType}`}`;
        const paginationString = createPartialURL(listParameters?.goToPage, listParameters?.pageSize, listParameters?.sortBy, filterOutNonMobileAccounts);
        const includes = "include=accountType";
        const accountFields = "fields[account]=accName,accActive,accountType,isPasswordCaptured";
        const accountTypeFields = "fields[accountType]=atDesc,atSysname,trnName";
        const url = `account?${includes}&${accountFields}&${accountTypeFields}${paginationString}`;
        const response = await apiConnector.fetchData(url);
        const json = await response.json();
        const pagination = createPagination(json.meta);

         return new Promise((resolve, reject) => {
            if (json.errors) {
                return reject({ status: response.status, message: getConstraintViolation(json.errors[0].detail) })
            }

            new Deserializer().deserialize(json, (error: any, data: any[]) => {

                if (error) {
                    return reject({ status: response.status, message: genericError });
                }

                const accounts: MobileAccount[] = data.map((account): MobileAccount => {
                    return {
                        id: account['id'],
                        name: account['acc-name'],
                        enabled: data['acc-active'] == 1,
                        description: account['account-type']['at-desc'] ?? '',
                        status: getActiveStatus(account['acc-active'] ? account['acc-active'].toString() : "0"),
                        accountTypeSysname: account['account-type']['at-sysname'],
                        accountTypeId: account['account-type']['id'],
                        accountTypeName: account['account-type']['trn-name'],
                        isPasswordCaptured: data['is-password-captured']
                        // added toString() conversion as getAccountStatus does a strict equality check(===),
                        // and compares a string(comparison value type) to a number(type of the value passed as the parameter),
                        // hence always returns inactive
                    }
                })

                return resolve({ rows: accounts, pagination });
            })
        });
    } catch (error) {
        return new Promise((resolve, reject) => {
            reject(processServerError(error));
        });
    }
}

/**
 * Fetches system account names with pagination. This includes mobile accounts as well as other system accounts.
 * @param listQuery
 * @returns
 */
export const getMobileAccountNamesWithPagination = async (listQuery?: ListQuery): Promise<{ rows: Name[], pagination: PaginationModel }> => {
    try {
        const filter: string = getFilterClause(listQuery?.filters, listQuery?.searchTerm);
        const filterAccountType = `filter=accountType.id=in=(${ACCOUNT_TYPES.ID_HID_MOBILE_ACCESS},${ACCOUNT_TYPES.ID_STID_MOBILE_ID},${ACCOUNT_TYPES.ID_MQTT_BROKER},${ACCOUNT_TYPES.ID_LDAP},${ACCOUNT_TYPES.ID_VINGCARD},${ACCOUNT_TYPES.ID_AUTH})`;
        const filterOutNonMobileAccounts = `${filter ? filter.replace('filter=', `${filterAccountType};`) : `&${filterAccountType}`}`;
        const paginationString = createPartialURL(listQuery?.goToPage, listQuery?.pageSize, listQuery?.sortBy, filterOutNonMobileAccounts);
        const accountFields = "fields[account]=accName";
        const url = `account?${paginationString}&${accountFields}`;
        const response = await apiConnector.fetchData(url);
        const json = await response.json();
        const newPagination = createPagination(json.meta);

        return new Promise((resolve, reject) => {
            if (json.errors) {
                return reject({ status: response.status, message: getConstraintViolation(json.errors[0].detail) })
            }

            new Deserializer().deserialize(json, (err: any, apiAccounts: any[]) => {
                if (err) {
                    return reject({ status: response.status, message: genericError });
                }

                const accounts: Name[] = apiAccounts.map((account): Name => {
                    return {
                        value: account['id'],
                        label: account['acc-name']
                    };
                });

                return resolve({ rows: accounts, pagination: newPagination });
            })
        })
    } catch (error) {
        return new Promise((resolve, reject) => {
            reject(processServerError(error));
        });
    }
}

export const getMobileAccountById = async (id: string, listParameters?: ListQuery): Promise<MobileAccount> => {
    try {
        const filter: string = getFilterClause(listParameters?.filters, listParameters?.searchTerm);
        const paginationString = createPartialURL(listParameters?.goToPage, listParameters?.pageSize, listParameters?.sortBy, filter);
        const accountFields = "fields[account]=accName,accActive,accountType,isPasswordCaptured";
        const accountTypeFields = "fields[accountType]=atDesc,atSysname,trnName";
        const url = `account/${id}?include=accountType${paginationString}&${accountFields}&${accountTypeFields}`;
        const response = await apiConnector.fetchData(url);
        const json = await response.json();

        return new Promise((resolve, reject) => {
            if (json.errors) {
                return reject({ status: response.status, message: getConstraintViolation(json.errors[0].detail) })
            }

            new Deserializer().deserialize(json, (err: any, data: any[]) => {
                if (err) {
                    return reject({ status: response.status, message: genericError });
                }

                const account: MobileAccount = {
                    id: data['id'],
                    name: data['acc-name'],
                    enabled: data['acc-active'] == 1,
                    description: data['account-type']['at-desc'],
                    accountTypeId: data['account-type']['id'],
                    accountTypeSysname: data['account-type']['at-sysname'],
                    accountTypeName: data['account-type']['trn-name'],
                    isPasswordCaptured: data['is-password-captured']
                }

                return resolve(account);
            });
        });
    } catch (error) {
        return new Promise((resolve, reject) => {
            reject(processServerError(error));
        });
    }
}

export const getMobileAccountConfigurations = async (accountId: string, accountTypeId: string): Promise<MobileAccessWrapper> => {
    try {
        const url = `accountValue?filter=account.id==${accountId}&include=accountAttribute,account,ksCertificate&fields[accountValue]=accountAttribute,avData,ksCertificate&fields[accountAttribute]=aaName`;
        const response = await apiConnector.fetchData(url);
        const json = await response.json();
        const pagination = createPagination(json.meta);

        return new Promise((resolve, reject) => {
            if (json.errors) {
                return reject({ status: response.status, message: getConstraintViolation(json.errors[0].detail) })
            }

            new Deserializer().deserialize(json, (err: any, apiMobileAccess: any[]) => {
                if (err) {
                    return reject({ status: response.status, message: genericError });
                }

                const mobileAccessAccount: MobileAccess[] = apiMobileAccess.map((account): MobileAccess => {
                    return {
                        id: account['id'],
                        name: account['account-attribute']['aa-name'],
                        accountAttributeId: account['account-attribute']['id'] ? account['account-attribute']['id'] : undefined,
                        accountValue: account['av-data'] ? account['av-data'] : undefined,
                        origValue: account['av-data'] ? account['av-data'] : undefined,
                        ksCertificateId: account['ks-certificate'] ? account['ks-certificate']['id'] : undefined
                    };
                });

                return resolve({ rows: mobileAccessAccount, pagination: pagination });
            });
        });
    } catch (error) {
        return new Promise((resolve, reject) => {
            reject(processServerError(error));
        });
    }
}

export const getIdentityProviderGroupsByAccountId = async (translate: Translator, listParameters?: ListQuery): Promise<IdentityProviderGroupWrapper> => {
    try {
        const filter: string = getFilterClause(listParameters?.filters, listParameters?.searchTerm);
        const partialUrl = createPartialURL(listParameters?.goToPage, listParameters?.pageSize, listParameters?.sortBy, filter);
        const accountFields = "fields[account]=accName";
        const profileFields = "fields[profile]=trnName";
        const identityProviderGroupFields = "fields[idpGroup]=idpGroupName,idpGroupDescription,account,profile";
        const includeFields = "include=account,profile";
        const url = `idpGroup?${includeFields}${partialUrl}&${accountFields}&${profileFields}&${identityProviderGroupFields}`;
        const response = await apiConnector.fetchData(url);
        const json = await response.json();
        const pagination = createPagination(json.meta);

        return new Promise((resolve, reject) => {
            if (json.errors) {
                return reject({ status: response.status, message: getConstraintViolation(json.errors[0].detail) })
            }

            new Deserializer().deserialize(json, (err: any, apiIdentityProvider: any[]) => {
                if (err) {
                    return reject({ status: response.status, message: genericError });
                }

                const identityProviderGroups: IdentityProviderGroup[] = apiIdentityProvider.map((groups): IdentityProviderGroup => {
                    return {
                        id: groups['id'],
                        idpGroupName: groups['idp-group-name'],
                        idpGroupDescription: groups['idp-group-description'] ?? undefined,
                        profileName: groups['profile'] ? groups['profile']["trn-name"] : undefined,
                        profileId: groups['profile'] ? groups['profile']["id"] : undefined,
                        accountName: groups['account']['accName'],
                        accountId: groups['account']['id'],
                    };
                });

                return resolve({ rows: identityProviderGroups, pagination: pagination });
            });
        });
    }
    catch (error) {
        return Promise.reject(processServerError(error));
    }
}

export const saveIdentityProviderGroupMap = async (mapDetails: IdentityProviderGroup, createNew = true): Promise<any> => {
    try {
        const newRecord = {
            data: {
                type: "idpGroup",
                id: mapDetails.id,
                attributes: {
                    idpGroupId: mapDetails.identityProviderGroupId,
                    idpGroupName: mapDetails.idpGroupName,
                    idpGroupDescription: mapDetails.idpGroupDescription,
                },
                relationships: {
                    account: {
                        data: {
                            type: "account",
                            id: mapDetails.accountId
                        }
                    },
                    profile: mapDetails.profileId ? {
                        data: {
                            type: "profile",
                            id: mapDetails.profileId
                        }
                    } : undefined
                }
            }
        }

        const url = `idpGroup${createNew ? "" : `/${mapDetails.id}`}`;
        let response: Response;
        await apiConnector.sendData(url, createNew, newRecord).then((res) => {
            response = res;
        }).catch((error) => {
            return new Promise((resolve, reject) => {
                reject(processServerError(error));
            });
        });

        return new Promise (async (resolve, reject) => {
            if (response) {
                if (createNew) {
                    const content = await response.json();
                    if (content.errors) {
                        return reject({
                            status: response.status,
                            message: getConstraintViolation(content.errors[0].detail),
                        });
                    }
                    new Deserializer().deserialize( content, (error, newMap) => {
                        if (error) {
                            return reject({
                                status: response.status,
                                id: newMap.id,
                                success: false,
                                message: genericError,
                            });
                        }
                        return resolve({ id: newMap.id, success: true });
                    });
                    return;
                }

                // An existing mapping was updated successfully
                if (response.status === NO_CONTENT) {
                    return resolve({ id: mapDetails.id, success: true });
                }

                const content = await response.json();
                return reject({
                    status: response.status,
                    id: mapDetails.id,
                    success: false,
                    message: getConstraintViolation(content.errors[0].detail),
                });
            }
            return reject({
                status: INTERNAL_SERVER_ERROR,
                id: mapDetails.id,
                success: false,
                message: genericError,
            });
        })
    }
    catch (error) {
        return Promise.reject(processServerError(error));
    }
}

const newBuildIdProviderGroup = (apiCredentials: any): IdentityProviderGroup => {
    return {
        id: apiCredentials['id'],
        idpGroupName: apiCredentials['idp-group-name'],
        idpGroupDescription: apiCredentials['idp-group-description'] ?? undefined,
        profileName: apiCredentials['profile'] ? apiCredentials['profile']["trn-name"] : undefined,
        profileId: apiCredentials['profile'] ? apiCredentials['profile']["id"] : undefined,
        accountName: apiCredentials['account']['acc-name'],
        accountId: apiCredentials['account']['id'],
    }
}

export const getIdentityProviderGroupsById = async (id: string): Promise<IdentityProviderGroup> => {
 try {
     const url = `idpGroup/${id}?include=profile,account&fields[account]=accName&fields[profile]=trnName&fields[idpGroup]=idpGroupName,idpGroupDescription,account,profile`;
     const response = await apiConnector.fetchData(url);
     const json = await response.json();
     return new Promise((resolve, reject) => {
         new Deserializer().deserialize(json, (error: any, apiCredentials: any) => {
             if (error) {
                 return reject({ status: response.status, message: genericError });
             }

             const group: IdentityProviderGroup = newBuildIdProviderGroup(apiCredentials);
             return resolve(group)
         });

     });
 }
 catch (error) {
     return Promise.reject(processServerError(error));
 }
}

export const deleteIdentityProviderGroupMap = async (id: string): Promise<{ id: string; success: boolean }> => {
    try {
        const url = `idpGroup/${id}`;
        const identityProviderGroup = {
            data: {
                type: "idpGroup",
                id: id,
            },
        };

        const response = await apiConnector.deleteData(url, identityProviderGroup);
        return new Promise(async (resolve, reject) => {
            if (response.status === NO_CONTENT) {
                return resolve({ id: id, success: true });
            }

            const json = await response.json();

            return reject({
                status: response.status,
                id: id,
                success: false,
                message: getConstraintViolation(json.errors[0].detail),
            });
        });
    } catch (error) {
        return Promise.reject(processServerError(error));
    }
}

export const getQueryParts = async (selectedPartNumber: string): Promise<any> => {
    try {
        const syncData = {
            data: {
                type: "executeCommand",
                id: 1,
                attributes: {
                    cmd: Commands.Account.label,
                    cmdSubType: HidMobileAccessSubCmds.PartNumbers,
                    partNumber: selectedPartNumber
                }
            }
        }

        const response: Response = await apiConnector.sendData("executeCommand", true, syncData);
        return new Promise(async (resolve, reject) => {
            if (response && response.status === CREATED) {
                const json = await response.json();

                // Handle json errors
                if (json.errors) {
                    return reject({ status: response.status, message: getConstraintViolation(json.errors[0].detail) })
                }

                new Deserializer().deserialize(json, (err: any, commandResult: any) => {
                    if (err) {
                        return reject({ status: response.status, success: false, messageKey: TranslationKeys.applicationError });
                    }

                    const cmdStatus = commandResult['cmd-status'];
                    const cmdStatusMsg = commandResult['cmd-status-msg'];

                    // Failed status
                    if (cmdStatus === "FAILED") {
                        return reject({ status: response.status, success: false, messageKey: cmdStatusMsg });
                    }

                    const queryList = commandResult['list'];

                    // Part numbers found
                    if (queryList && queryList.length > 0) {
                        const queryParts = queryList.map((partValue) => {
                            const partId = partValue.split(';')[0];
                            const credentialsAvailable = partValue.split(';')[1];
                            return {
                                value: partId,
                                label: partId,
                                count: credentialsAvailable
                            }
                        })

                        return resolve(queryParts);
                    }
                    // No part numbers available
                    else {
                        return reject({ status: response.status, success: false, messageKey: TranslationKeys.noPartsFound });
                    }
                });
            }
            // Invalid response or status
            else {
                return reject({ status: response.status, success: false, messageKey: TranslationKeys.applicationError });
            }
        });
    } catch (error) {
        return new Promise((resolve, reject) => {
            reject(processServerError(error));
        });
    }
}

/**
 * This is temporary. Just for v1.5 which needs to auto select both the site and reader configuration id.
 * In future we need to drop this and rather query the sites and let the user select a site, query the reader config and let
 * the user select one, then query the credits for those selections.
 * @param selectedSiteId - if supplied it means we will save the selected site id
 * @returns
 */
export const queryStidDetails = async( selectedSiteId: string, translate: Translator ): Promise<any> => {
    try {
        const syncData = {
            data: {
                type: "executeCommand",
                id: 1,
                attributes: {
                    cmd: Commands.Account.label,
                    cmdSubType: StidMobileSubCmds.Get_Details,
                    partNumber: selectedSiteId
                }
            }
        }

        const response: Response = await apiConnector.sendData("executeCommand", true, syncData);
        return new Promise(async (resolve, reject) => {
            if (response && response.status === CREATED) {
                const json = await response.json();

                // Handle json errors
                if (json.errors) {
                    return reject({ status: response.status, message: getConstraintViolation(json.errors[0].detail) })
                }

                new Deserializer().deserialize(json, (error: any, data: any) => {
                    if (error) {
                        return reject({ status: response.status, success: false, messageKey: TranslationKeys.applicationError });
                    }

                    const cmdStatus = data['cmd-status'];
                    const cmdStatusMsg = data['cmd-status-msg'];

                    // Failed status
                    if (cmdStatus === "FAILED") {
                        return reject({ status: response.status, success: false, messageKey: cmdStatusMsg });
                    }

                    const innerJson = data['json-response'];
                    const jsonResponse = (innerJson === '') ? '' : JSON.parse(innerJson);

                    if (jsonResponse) {
                        const data = {
                            ...jsonResponse,
                            creditsAvailable: translate.byKeyFormatted(TranslationKeys.stidCreditsAvailable, { credits: jsonResponse.creditsAvailable, credentials: parseInt(jsonResponse.creditsAvailable) / 5 })
                        }
                        return resolve(data);
                    }
                    // An error occurred
                    else {
                        return reject({ status: response.status, success: false, messageKey: TranslationKeys.noPartsFound });
                    }
                });
            }
            // Invalid response or status
            else {
                return reject({ status: response.status, success: false, messageKey: TranslationKeys.applicationError });
            }
        });
    } catch (error) {
        return new Promise((resolve, reject) => {
            reject(processServerError(error));
        });
    }
}

/**
 *
 * @param selectedSiteId - if supplied it means we will save the selected site id
 * @returns
 */
export const getStidSites = async( selectedSiteId: string ): Promise<any> => {
    try {
        const syncData = {
            data: {
                type: "executeCommand",
                id: 1,
                attributes: {
                    cmd: Commands.Account.label,
                    cmdSubType: StidMobileSubCmds.Get_Sites,
                    partNumber: selectedSiteId
                }
            }
        }

        const response: Response = await apiConnector.sendData("executeCommand", true, syncData);
        return new Promise(async (resolve, reject) => {
            if (response && response.status === CREATED) {
                const json = await response.json();

                // Handle json errors
                if (json.errors) {
                    return reject({ status: response.status, message: getConstraintViolation(json.errors[0].detail) })
                }

                new Deserializer().deserialize(json, (error: any, data: any) => {
                    if (error) {
                        return reject({ status: response.status, success: false, messageKey: TranslationKeys.applicationError });
                    }

                    const cmdStatus = data['cmd-status'];
                    const cmdStatusMsg = data['cmd-status-msg'];

                    // Failed status
                    if (cmdStatus === "FAILED") {
                        return reject({ status: response.status, success: false, messageKey: cmdStatusMsg });
                    }

                    const innerJson = data['json-response'];
                    const sitesJson = (innerJson === '') ? '' : JSON.parse(innerJson);

                    // Sites found
                    if (sitesJson && sitesJson.length > 0) {
                        const sitesList = sitesJson.map((site) => {
                            const siteId = site.SiteId;
                            const siteName = site.SiteName;
                            return {
                                value: siteId,
                                label: siteName,
                            }
                        })
                        return resolve(sitesList);
                    }
                    // No sites available
                    else {
                        return reject({ status: response.status, success: false, messageKey: TranslationKeys.noPartsFound });
                    }
                });
            }
            // Invalid response or status
            else {
                return reject({ status: response.status, success: false, messageKey: TranslationKeys.applicationError });
            }
        });
    } catch (error) {
        return new Promise((resolve, reject) => {
            reject(processServerError(error));
        });
    }
}

export const storeMobileAccount = async (account) => {
    try {
        const accountDetailFields = Object.keys(account.details);
        const accountDetailPromises = accountDetailFields.map(async (accountDetailFieldKey) => {
            const id = account.details[accountDetailFieldKey].id;
            const value = account.details[accountDetailFieldKey].value?.trim();
            const accountValueId = account.details[accountDetailFieldKey].accountValueId;

            if (value) {
                const newRecord = {
                    data: {
                        type: "accountValue",
                        id: accountValueId,
                        attributes: {
                            avData: value,
                        },
                        relationships: {},
                    },
                } // Don't worry about populating the ksCertificate, backend will handle that on save.

                const createAccountValue = id ? false : true;
                const url = `accountValue${createAccountValue ? '' : `/${accountValueId}`}`;
                const response: Response = await apiConnector.sendData(url, createAccountValue, newRecord);

                return new Promise<StoreEntityResult>(async (resolve, reject) => {
                    if (response) {
                        if (createAccountValue) {
                            const content = await response.json();

                            if (content.errors) {
                                return reject({ status: response.status, message: getConstraintViolation(content.errors[0].detail) })
                            }

                            new Deserializer().deserialize(content, (error, newAccount) => {
                                if (error) {
                                    return reject({ status: response.status, id: id, success: false });
                                }

                                return resolve({ id: newAccount.id, success: true });
                            })

                            return;
                        }

                        if (response.status === NO_CONTENT) {
                            return resolve({ id: id, success: true })
                        }

                        const json = await response.json();
                        return reject({ status: response.status, id, success: false, message: getConstraintViolation(json.errors[0].detail) });
                    }

                    return reject({ status: INTERNAL_SERVER_ERROR, id: id, success: false });
                })
            }
        });

        return Promise.all(accountDetailPromises);
    } catch (error) {
        return new Promise((resolve, reject) => {
            reject(processServerError(error));
        });
    }
}

export const editAccount = async (value: MobileAccount) => {
    try {
        const url = `account/${value.id}`;

        const updatedRecord = {
            "data": {
                "type": "account",
                "id": value.id,

                "attributes": {
                    ...(value.name && {accName: value.name}),
                    ...(value.enabled !==undefined && {accActive: value.enabled ? 1: 0}),
                    ...(value.tempPassword !==undefined && {tempPassword: value.tempPassword}),
                },
                "relationships": {

                }
            }
        }

        // Sending data and waiting for response
        const response = await apiConnector.sendData(url, false, updatedRecord);

        // Checking if the response status is a fail and throwing an error if it is
        if (response.status !== NO_CONTENT) {
            const json = await response.json();
            return new Promise((resolve, reject) => {
                return reject({status: response.status, message: getConstraintViolation(json.errors[0].detail) })
            })
        }

        return response
    } catch (error) {
        return new Promise(async (resolve, reject) => {
            reject(processServerError(error));
        });
    }
}
